/**
 * 看图组件的纯逻辑（无 React / 无 DOM 依赖）。
 *
 * 交互照 `hub_pages/imgview.js`：
 *   · 缩放 1–8 倍，滚轮以指针为锚点（按住放大镜时滚轮改成调放大倍数 1.5–8×）
 *   · 双击在指针处放大到 2.5 倍 / 已放大则还原
 *   · 放大后拖动平移，平移夹在「图片边缘不离开画面」的范围内
 *   · 长按 320ms 出圆形放大镜；按下后移动超过 8px 就当拖动、不出镜
 *   · 放大镜默认在指针上方，贴近窗口顶部自动翻到下方
 *   · 全景（宽高比 > 2 或显式 panorama）：整图按容器高度铺满，横向拖动环视
 *
 * 组件只负责把这里的返回值塞进 state / style，所有算术都有单测。
 */

export interface ImageViewerItem {
  src: string;
  title?: string;
  /** 底部 bar 的第二行（路径 / 槽位之类的元数据）。 */
  subtitle?: string;
  /** 显式全景：宽高比还没读到时也能直接进环视模式。 */
  panorama?: boolean;
}

export interface Size {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

/** 和 DOMRect 同形（只取组件真正用到的四个字段）。 */
export interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** 缩放 + 平移（屏幕像素，相对 stage 中心）。 */
export interface ZoomState {
  s: number;
  x: number;
  y: number;
}

/** 缩放上下限（imgview.js：1–8 倍）。 */
export const MIN_ZOOM = 1;
export const MAX_ZOOM = 8;
/** 放大镜倍数区间（imgview.js：1.5–8×，默认 3×）。 */
export const MAGNIFIER_MIN_ZOOM = 1.5;
export const MAGNIFIER_MAX_ZOOM = 8;
export const MAGNIFIER_ZOOM = 3;
/** 长按多久出放大镜（毫秒）与「按了但算拖动」的位移阈值（屏幕像素）。 */
export const PRESS_MS = 320;
export const PRESS_SLOP = 8;
/** 双击放大到多少倍。 */
export const DOUBLE_CLICK_ZOOM = 2.5;
/** 宽高比超过这个值就当全景（还能被 `panorama: true` 覆盖）。 */
export const PANORAMA_RATIO = 2;
/** 普通模式下图片四周的留白（和 CSS `.dp-iv-frame { inset }` 保持一致）。 */
export const STAGE_PADDING = 14;
/** 单指拖动超过这个距离才算拖动（imgview.js 的 `moved`）。 */
export const DRAG_SLOP = 4;

export function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  const clamped = Math.min(max, Math.max(min, value));
  // 把 -0 归一成 0：否则 `toEqual({ x: 0 })` 之类的断言会被 Object.is 判成不等。
  return clamped === 0 ? 0 : clamped;
}

/** 缩放夹到 1–8 倍。 */
export function clampZoom(scale: number): number {
  return clamp(scale, MIN_ZOOM, MAX_ZOOM);
}

/** 滚轮 → 新倍数：指针在图上滚，ctrl/⌘ 走更细的步长（触控板捏合）。 */
export function wheelZoomFactor(deltaY: number, ctrlKey = false): number {
  return Math.exp(-deltaY / (ctrlKey ? 100 : 300));
}

/** 放大镜显示中的滚轮：调放大倍数（1.5–8×）。 */
export function magnifierZoomFactor(current: number, deltaY: number): number {
  return clamp(current * Math.exp(-deltaY / 400), MAGNIFIER_MIN_ZOOM, MAGNIFIER_MAX_ZOOM);
}

/**
 * 图片在 stage 里的显示尺寸（scale = 1 时）。
 *
 * · 普通：contain（四周留 14px），和 `<img object-fit: contain>` 一致
 * · 全景：按容器高度铺满，宽度按原图比例（通常会溢出，所以能横向拖动）
 */
export function contentBox(natural: Size, viewport: Size, panorama = false): Size {
  if (natural.width <= 0 || natural.height <= 0) return { width: 0, height: 0 };
  if (viewport.width <= 0 || viewport.height <= 0) return { width: 0, height: 0 };
  if (panorama) {
    return { width: (viewport.height * natural.width) / natural.height, height: viewport.height };
  }
  const scale = Math.min(
    (viewport.width - STAGE_PADDING * 2) / natural.width,
    (viewport.height - STAGE_PADDING * 2) / natural.height,
  );
  return { width: natural.width * scale, height: natural.height * scale };
}

/** 平移的两轴极限：图片边缘最多贴到 stage 边缘（imgview.js 的 mx / my）。 */
export function panLimit(
  content: Size,
  viewport: Size,
  scale = MIN_ZOOM,
  padding = STAGE_PADDING,
): { x: number; y: number } {
  const gap = padding * 2;
  return {
    x: Math.max(0, (content.width * scale - (viewport.width - gap)) / 2),
    y: Math.max(0, (content.height * scale - (viewport.height - gap)) / 2),
  };
}

/** 把缩放 / 平移夹回合法范围。 */
export function clampPan(
  zoom: ZoomState,
  content: Size,
  viewport: Size,
  padding = STAGE_PADDING,
): ZoomState {
  const limit = panLimit(content, viewport, zoom.s, padding);
  return {
    s: zoom.s,
    x: clamp(zoom.x, -limit.x, limit.x),
    y: clamp(zoom.y, -limit.y, limit.y),
  };
}

/**
 * 以屏幕上的某点为锚点缩放（滚轮 / 双击 / 键盘都走这里）。
 * 等价于 imgview.js 的 `zAt`：锚点在图片上的位置缩放前后不动。
 */
export function zoomAt(
  zoom: ZoomState,
  nextScale: number,
  point: Point,
  viewport: Rect,
  content: Size,
  padding = STAGE_PADDING,
): ZoomState {
  const scale = clampZoom(nextScale);
  const ratio = zoom.s > 0 ? scale / zoom.s : 1;
  const cx = point.x - viewport.left - viewport.width / 2;
  const cy = point.y - viewport.top - viewport.height / 2;
  return clampPan(
    {
      s: scale,
      x: cx - (cx - zoom.x) * ratio,
      y: cy - (cy - zoom.y) * ratio,
    },
    content,
    viewport,
    padding,
  );
}

/** 平移（拖动）：只改 x / y，最后夹回边界。 */
export function panBy(
  zoom: ZoomState,
  dx: number,
  dy: number,
  content: Size,
  viewport: Size,
  padding = STAGE_PADDING,
): ZoomState {
  return clampPan({ s: zoom.s, x: zoom.x + dx, y: zoom.y + dy }, content, viewport, padding);
}

/** 还原到 1 倍居中。 */
export function resetZoom(): ZoomState {
  return { s: MIN_ZOOM, x: 0, y: 0 };
}

/** 双指缩放：按两指距离比改倍数，再跟上质心位移。 */
export function pinchZoom(
  base: ZoomState,
  start: { distance: number; centroid: Point },
  next: { distance: number; centroid: Point },
  viewport: Rect,
  content: Size,
  padding = STAGE_PADDING,
): ZoomState {
  if (start.distance <= 0) return clampPan(base, content, viewport, padding);
  const scaled = zoomAt(
    base,
    base.s * (next.distance / start.distance),
    start.centroid,
    viewport,
    content,
    padding,
  );
  return clampPan(
    {
      s: scaled.s,
      x: scaled.x + (next.centroid.x - start.centroid.x),
      y: scaled.y + (next.centroid.y - start.centroid.y),
    },
    content,
    viewport,
    padding,
  );
}

/**
 * 把图片元素当前的 DOMRect 换算成「真正显示出来的图」的矩形。
 * 缩放 / 平移之后 DOMRect 已经是变换后的，所以放大镜在任意状态下都对得上。
 */
export function containRect(box: Rect, natural: Size, fit: "contain" | "cover" = "contain"): Rect {
  if (natural.width <= 0 || natural.height <= 0 || box.width <= 0 || box.height <= 0) {
    return { ...box };
  }
  const scale =
    fit === "cover"
      ? Math.max(box.width / natural.width, box.height / natural.height)
      : Math.min(box.width / natural.width, box.height / natural.height);
  const width = natural.width * scale;
  const height = natural.height * scale;
  return {
    left: box.left + (box.width - width) / 2,
    top: box.top + (box.height - height) / 2,
    width,
    height,
  };
}

/** 指针是否落在真正显示出来的图上（不含留白 / letterbox）。 */
export function isPointOnImage(point: Point, display: Rect): boolean {
  return (
    point.x >= display.left &&
    point.x <= display.left + display.width &&
    point.y >= display.top &&
    point.y <= display.top + display.height
  );
}

/** 屏幕点 → 原图像素坐标（贴边时夹住）。 */
export function imagePixelAt(point: Point, display: Rect, natural: Size): Point {
  if (display.width <= 0 || display.height <= 0) return { x: 0, y: 0 };
  return {
    x: clamp(((point.x - display.left) / display.width) * natural.width, 0, natural.width),
    y: clamp(((point.y - display.top) / display.height) * natural.height, 0, natural.height),
  };
}

/** 放大镜直径（imgview.js：窗口短边的 32%，夹在 150–250px）。 */
export function magnifierDiameter(width: number, height: number): number {
  return Math.max(150, Math.min(250, Math.round(Math.min(width, height) * 0.32)));
}

/**
 * 放大镜落点：默认在指针上方，贴近窗口顶部时翻到下方；最后整体夹进窗口。
 * 返回值是 `left / top`（圆形外框左上角）与「是否翻到了下方」。
 */
export function magnifierPlacement(
  point: Point,
  diameter: number,
  viewport: Size,
  gap = 16,
  padding = 8,
): { left: number; top: number; flipped: boolean } {
  const radius = diameter / 2;
  let centerX = point.x;
  let centerY = point.y - gap - radius;
  const flipped = centerY - radius < padding;
  if (flipped) centerY = point.y + gap + radius;
  centerX = clamp(centerX, radius + padding, Math.max(radius + padding, viewport.width - radius - padding));
  centerY = clamp(centerY, radius + padding, Math.max(radius + padding, viewport.height - radius - padding));
  return { left: centerX - radius, top: centerY - radius, flipped };
}

export interface MagnifierSource {
  /** 镜里取的原图区域（左上角 + 边长，原图像素）。 */
  x: number;
  y: number;
  size: number;
  /** 贴边时真正取到的原图区域（drawImage 的 sx / sy / sw / sh）。 */
  clip: { x: number; y: number; width: number; height: number };
  /** 直接喂给 `ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh)` 的后四个参数。 */
  draw: { dx: number; dy: number; dw: number; dh: number };
  /** 指针在原图上的像素坐标。 */
  pixel: Point;
}

/**
 * 放大镜里要画的原图区域。
 *
 * imgview.js：镜里显示「屏幕上 D/MAG 的区域」，D 是镜的直径；贴边时把那块区域夹进原图，
 * 于是 drawImage 的目标矩形也跟着偏移（边缘留白而不是拉伸）。
 */
export function magnifierSource(
  point: Point,
  diameter: number,
  magnification: number,
  display: Rect,
  natural: Size,
): MagnifierSource | null {
  if (diameter <= 0 || natural.width <= 0 || natural.height <= 0) return null;
  if (display.width <= 0 || display.height <= 0) return null;
  const mag = clamp(magnification, MAGNIFIER_MIN_ZOOM, MAGNIFIER_MAX_ZOOM);
  const screenPerPixel = display.width / natural.width;
  const size = diameter / mag / screenPerPixel;
  const pixel = imagePixelAt(point, display, natural);
  const x = pixel.x - size / 2;
  const y = pixel.y - size / 2;
  const left = Math.max(0, x);
  const top = Math.max(0, y);
  const right = Math.min(natural.width, x + size);
  const bottom = Math.min(natural.height, y + size);
  return {
    x,
    y,
    size,
    pixel,
    clip: { x: left, y: top, width: right - left, height: bottom - top },
    draw: {
      dx: ((left - x) / size) * diameter,
      dy: ((top - y) / size) * diameter,
      dw: ((right - left) / size) * diameter,
      dh: ((bottom - top) / size) * diameter,
    },
  };
}

/** ← → 切换：首尾循环；空列表返回 0。 */
export function nextIndex(index: number, delta: number, length: number): number {
  if (length <= 0) return 0;
  return (((index + delta) % length) + length) % length;
}

/** 是否按全景处理：显式 `panorama: true`，或者宽高比 > 2。 */
export function isPanorama(
  item: { panorama?: boolean },
  natural?: Size | null,
): boolean {
  if (item.panorama) return true;
  if (!natural || natural.width <= 0 || natural.height <= 0) return false;
  return natural.width / natural.height > PANORAMA_RATIO;
}

export type ViewerCommand = "close" | "reset" | "zoom-in" | "zoom-out" | "prev" | "next";

/**
 * 键盘映射（imgview.js）：Esc / 0 / + - / ← →。
 * 带 ⌘ / Ctrl / Alt 的组合键与输入框里一律不接管。
 */
export function viewerKey(
  key: string,
  options: { modifier?: boolean; editing?: boolean } = {},
): ViewerCommand | null {
  if (options.modifier || options.editing) return null;
  switch (key) {
    case "Escape":
      return "close";
    case "0":
      return "reset";
    case "+":
    case "=":
      return "zoom-in";
    case "-":
    case "_":
      return "zoom-out";
    case "ArrowLeft":
      return "prev";
    case "ArrowRight":
      return "next";
    default:
      return null;
  }
}

/** 底部 bar 的百分比文案。 */
export function formatZoom(scale: number): string {
  return `${Math.round(scale * 100)}%`;
}

/** 放大镜上的倍数文案：`3×` / `3.9×`（imgview.js 的 `toFixed(1)`）。 */
export function formatMagnification(magnification: number): string {
  return `${magnification.toFixed(1).replace(/\.0$/, "")}×`;
}

/** 底部切换文案：`(2/5)`，单张时为空。 */
export function formatCounter(index: number, length: number): string {
  return length > 1 ? `(${index + 1}/${length})` : "";
}
